import type { SessionStatus } from "@velizhanin-dev/bot-scenario"
import type { DraftVariable } from "@/entities/bot-builder"
import { apiClient } from "@/shared/api/client"

/** Состояние диалога: статус движка или «заблокировал бота». */
export type MemberState = SessionStatus | "BLOCKED"

export type AudienceStateFilter = "waiting" | "idle" | "blocked"
export type VariableFilterOp = "set" | "empty" | "equals" | "contains"

export interface VariableFilter {
	variable: string
	op: VariableFilterOp
	value?: string
}

/** Кто человек в приложении: условия по аккаунту с тем же Telegram ID. */
export interface AudienceSegment {
	variableRules?: VariableFilter[]
	channel?: "member" | "not_member" | "no_account"
	readWithinDays?: number
	notReadDays?: number
	registeredFrom?: string
	registeredTo?: string
	nicheId?: string
	minReads?: number
	favorites?: "has" | "none"
	beginner?: "not_started" | "started"
	beginnerMinSteps?: number
}

export const SEGMENT_KEYS = [
	"variableRules",
	"channel",
	"readWithinDays",
	"notReadDays",
	"registeredFrom",
	"registeredTo",
	"nicheId",
	"minReads",
	"favorites",
	"beginner",
	"beginnerMinSteps",
] as const satisfies readonly (keyof AudienceSegment)[]

export interface AudienceFilter extends Partial<VariableFilter>, AudienceSegment {
	search?: string
	state?: AudienceStateFilter
	/** id метки: только пользователи с этой меткой. */
	tagId?: string
	/** Код UTM-метки: только пришедшие по ней. */
	entryLink?: string
	/** id блока: только те, кто его прошёл. */
	passedNodeId?: string
	/** id блока: только те, кто сейчас застрял на нём. */
	stuckNodeId?: string
	/** Рассылка «конкретным пользователям»: id сессий. */
	sessionIds?: string[]
	/** Впервые написали боту после этого момента (ISO). */
	joinedAfter?: string
	/** Писали боту после этого момента (ISO). */
	activeAfter?: string
	/** Рассылка не дошла из-за ошибки. */
	failedInBroadcastId?: string
	/** Не были среди получателей этой рассылки. */
	excludeBroadcastId?: string
	/** Не писали боту последние N дней. */
	inactiveDays?: number
	/** Платили ли в боте; тестовые платежи не считаются. */
	payments?: "has" | "none"
}

export interface AudienceMember {
	id: string
	telegramUserId: string
	firstName: string | null
	lastName: string | null
	username: string | null
	state: MemberState
	vars: Record<string, string | number | boolean>
	createdAt: string
	lastEventAt: string | null
	blockedAt: string | null
	/** В копейках. */
	paidAmount: number
	/** Код метки, по которой человек пришёл; null: пришёл сам. */
	entryLink: string | null
}

export interface AudienceVariable {
	name: string
	type: DraftVariable["type"]
	description: string | null
}

export interface AudienceList {
	total: number
	items: AudienceMember[]
	variables: AudienceVariable[]
}

export interface AudienceEvent {
	id: string
	/** IN: от пользователя, OUT: от бота. */
	direction: "IN" | "OUT"
	type: string
	text: string | null
	data: Record<string, unknown> | null
	nodeId: string | null
	createdAt: string
}

export interface AudienceCard {
	member: AudienceMember
	variables: AudienceVariable[]
	payments: {
		id: string
		title: string
		amount: number
		currency: string
		status: "PENDING" | "PAID"
		testMode: boolean
		createdAt: string
		paidAt: string | null
	}[]
	/** Группы человека в сценарии бота. */
	tags: { id: string; name: string }[]
	activity: { messagesIn: number; messagesOut: number; buttonClicks: number }
	/** Аккаунт приложения с тем же Telegram ID; null: человека там нет. */
	app: {
		registeredAt: string
		isChannelMember: boolean
		channelCheckedAt: string | null
		views: number
		favorites: number
		lastViewedAt: string | null
	} | null
	events: AudienceEvent[]
	hasOlderEvents: boolean
}

export const audienceKeys = {
	all: (botId: string) => ["bot-builder", "audience", botId] as const,
	count: (botId: string) => ["bot-builder", "audience", botId, "count"] as const,
	list: (botId: string, filter: AudienceFilter) =>
		["bot-builder", "audience", botId, "list", filter] as const,
	saved: (botId: string) => ["bot-builder", "audience", botId, "saved-filters"] as const,
	member: (botId: string, sessionId: string) =>
		["bot-builder", "audience", botId, "member", sessionId] as const,
	file: (botId: string, sessionId: string, fileId: string) =>
		["bot-builder", "audience", botId, "file", sessionId, fileId] as const,
}

const audiencePath = (botId: string, path = "") => `/admin/custom-bots/${botId}/audience${path}`

const filterParams = (filter: AudienceFilter) =>
	Object.fromEntries(
		Object.entries(filter)
			.filter(([, value]) => value !== undefined && value !== "")
			// Список id в query: одной строкой через запятую, api разбирает её обратно.
			.map(([key, value]) => [
				key,
				key === "variableRules"
					? JSON.stringify(value)
					: Array.isArray(value)
						? value.join(",")
						: value,
			]),
	)

export const listAudience = async (
	botId: string,
	filter: AudienceFilter,
	page: { skip: number; take: number; sort?: string },
) =>
	(
		await apiClient.get<AudienceList>(audiencePath(botId), {
			params: { ...filterParams(filter), ...page },
		})
	).data

export const getAudienceMember = async (botId: string, sessionId: string) =>
	(await apiClient.get<AudienceCard>(audiencePath(botId, `/${sessionId}`))).data

/** Добавить в группу или убрать из неё всех, кто под фильтром. */
export const bulkTagMembers = async (
	botId: string,
	tagId: string,
	action: "add" | "remove",
	filter: AudienceFilter,
) =>
	(
		await apiClient.post<{ changed: number }>(audiencePath(botId, `/tags/${tagId}/bulk`), {
			action,
			filter,
		})
	).data

export interface TagByListResult {
	found: number
	added: number
	alreadyIn: number
	notFound: string[]
	unreadable: string[]
}

export const tagMembersByList = async (
	botId: string,
	tagId: string,
	identifiers: string[],
	apply: boolean,
) =>
	(
		await apiClient.post<TagByListResult>(audiencePath(botId, `/tags/${tagId}/members`), {
			identifiers,
			apply,
		})
	).data

export interface SavedFilter {
	id: string
	name: string
	filter: AudienceFilter
	createdAt: string
}

const savedFiltersPath = (botId: string, path = "") =>
	`/admin/custom-bots/${botId}/audience-filters${path}`

export const listSavedFilters = async (botId: string) =>
	(await apiClient.get<SavedFilter[]>(savedFiltersPath(botId))).data

export const createSavedFilter = async (botId: string, name: string, filter: AudienceFilter) =>
	(await apiClient.post<SavedFilter>(savedFiltersPath(botId), { name, filter })).data

export const deleteSavedFilter = async (botId: string, id: string) => {
	await apiClient.delete(savedFiltersPath(botId, `/${id}`))
}

export const setMemberTags = async (botId: string, sessionId: string, tagIds: string[]) =>
	(
		await apiClient.put<{ tags: { id: string; name: string }[] }>(
			audiencePath(botId, `/${sessionId}/tags`),
			{ tagIds },
		)
	).data

export const getAudienceFile = async (botId: string, sessionId: string, fileId: string) =>
	(
		await apiClient.get<Blob>(
			audiencePath(botId, `/${sessionId}/files/${encodeURIComponent(fileId)}`),
			{ responseType: "blob" },
		)
	).data

function fileNameFrom(disposition: unknown, fallback: string): string {
	if (typeof disposition !== "string") return fallback
	const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
	if (encoded) {
		try {
			return decodeURIComponent(encoded)
		} catch {
			// битое имя: берём простое
		}
	}
	return disposition.match(/filename="([^"]+)"/i)?.[1] ?? fallback
}

export const downloadAudienceCsv = async (botId: string, filter: AudienceFilter) => {
	const response = await apiClient.get<Blob>(audiencePath(botId, "/export.csv"), {
		params: filterParams(filter),
		responseType: "blob",
	})
	return {
		blob: response.data,
		fileName: fileNameFrom(response.headers["content-disposition"], "audience.csv"),
	}
}

/** Сохранить файл из памяти: ссылка с download, адрес освобождается чуть позже клика. */
export function saveBlob(blob: Blob, fileName: string) {
	const url = URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = url
	link.download = fileName
	document.body.append(link)
	link.click()
	link.remove()
	window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}
